import {
  Avatar,
  Box,
  Button,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import ShieldRoundedIcon from '@mui/icons-material/ShieldRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import MenuOpenRoundedIcon from '@mui/icons-material/MenuOpenRounded';
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { adminSections } from '../../admin/adminSections';
import { clearAdminSession } from '../../services/adminAuthService';
import './AdminLayout.scss';

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  const activeSection =
    adminSections.find((section) => location.pathname === section.path) ??
    adminSections[0];

  const sidebarContent = (
    <>
      <Box className='admin-dashboard-brand'>
        <Avatar className='admin-dashboard-brandAvatar'>
          <ShieldRoundedIcon />
        </Avatar>
        <Box className='admin-dashboard-brandCopy'>
          <Typography className='admin-dashboard-brandTitle'>
            Pappad Admin
          </Typography>
          <Typography className='admin-dashboard-brandSubtitle'>
            Control center
          </Typography>
        </Box>
      </Box>

      <Typography className='admin-dashboard-menuLabel'>
        Management menu
      </Typography>

      <List className='admin-dashboard-menu'>
        {adminSections.map((section) => (
          <Tooltip
            key={section.id}
            title={!isMobile && isSidebarCollapsed ? section.label : ''}
            placement='right'
          >
            <ListItemButton
              component={NavLink}
              to={section.path}
              onClick={() => setIsMobileSidebarOpen(false)}
              className={({ isActive }) =>
                isActive
                  ? 'admin-dashboard-menuItem admin-dashboard-menuItem--active'
                  : 'admin-dashboard-menuItem'
              }
            >
              <ListItemIcon className='admin-dashboard-menuIcon'>
                {section.icon}
              </ListItemIcon>
              <ListItemText primary={section.label} />
            </ListItemButton>
          </Tooltip>
        ))}
      </List>
    </>
  );

  return (
    <Box className='admin-dashboard-page'>
      <Box
        className={`admin-dashboard-shell${
          !isMobile && isSidebarCollapsed
            ? ' admin-dashboard-shell--sidebarCollapsed'
            : ''
        }`}
      >
        {!isMobile ? (
          <aside className='admin-dashboard-sidebar'>{sidebarContent}</aside>
        ) : null}

        <Drawer
          anchor='left'
          open={isMobile && isMobileSidebarOpen}
          onClose={() => setIsMobileSidebarOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{ className: 'admin-dashboard-mobileDrawer' }}
        >
          <Box className='admin-dashboard-sidebar admin-dashboard-sidebar--drawer'>
            {sidebarContent}
          </Box>
        </Drawer>

        <Box className='admin-dashboard-main'>
          <header className='admin-dashboard-header'>
            <Box className='admin-dashboard-headerIntro'>
              <IconButton
                className='admin-dashboard-toggle'
                onClick={() =>
                  isMobile
                    ? setIsMobileSidebarOpen(true)
                    : setIsSidebarCollapsed((value) => !value)
                }
                aria-label={
                  isMobile
                    ? 'Open sidebar menu'
                    : isSidebarCollapsed
                      ? 'Expand sidebar'
                      : 'Collapse sidebar'
                }
              >
                {isMobile ? (
                  <MenuRoundedIcon />
                ) : isSidebarCollapsed ? (
                  <MenuRoundedIcon />
                ) : (
                  <MenuOpenRoundedIcon />
                )}
              </IconButton>

              <Box>
                <Typography className='admin-dashboard-headerKicker'>
                  Admin workspace
                </Typography>
                <Typography variant='h4' className='admin-dashboard-headerTitle'>
                  {activeSection.label}
                </Typography>
                <Typography className='admin-dashboard-headerSubtitle'>
                  {activeSection.description}
                </Typography>
              </Box>
            </Box>

            <Button
              variant='outlined'
              startIcon={<LogoutRoundedIcon />}
              className='admin-dashboard-logout'
              onClick={() => {
                clearAdminSession();
                navigate('/myadmin', { replace: true });
              }}
            >
              Logout
            </Button>
          </header>

          <Box key={location.pathname} className='route-motion-screen route-motion-screen--admin'>
            <Outlet />
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
